import type { z } from 'zod'
import { tarslogs, tarslogsEnabled } from '@/plugins/tarslogs'

interface ApiError {
  error?: string
  message?: string
}

export function useApi() {
  const baseUrl = import.meta.env.VITE_API_URL || '/api'

  async function request<T>(
    path: string,
    options?: RequestInit,
    schema?: z.ZodSchema<T>
  ): Promise<T> {
    const url = `${baseUrl}${path}`

    const res = await fetch(url, {
      ...options,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers
      }
    })

    const data = await res.json()

    if (!res.ok) {
      const apiError = data as ApiError
      const errorMsg = apiError.error || apiError.message || `Request failed: ${res.status}`
      if (tarslogsEnabled && res.status >= 500) {
        tarslogs.captureError(new Error(errorMsg), {
          source: 'api',
          method: options?.method || 'GET',
          path,
          status: res.status,
        })
      }
      throw new Error(errorMsg)
    }

    return schema ? schema.parse(data) : (data as T)
  }

  return {
    get: <T>(path: string, schema?: z.ZodSchema<T>) =>
      request<T>(path, { method: 'GET' }, schema),

    post: <T>(path: string, body: unknown, schema?: z.ZodSchema<T>) =>
      request<T>(path, { method: 'POST', body: JSON.stringify(body) }, schema),

    put: <T>(path: string, body: unknown, schema?: z.ZodSchema<T>) =>
      request<T>(path, { method: 'PUT', body: JSON.stringify(body) }, schema),

    patch: <T>(path: string, body: unknown, schema?: z.ZodSchema<T>) =>
      request<T>(path, { method: 'PATCH', body: JSON.stringify(body) }, schema),

    delete: <T>(path: string, schema?: z.ZodSchema<T>) =>
      request<T>(path, { method: 'DELETE' }, schema)
  }
}
